import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useMarketStrip, { resolveQuote } from "../../hooks/useMarketStrip";
import { STOCK_SESSIONS } from "../../utils/marketStatus";
import { priceDecimals } from "../../utils/orderFormat";
import { dayChangeColor } from "../Watchlist/watchlistFormat";

// Home Markets strip. Client: "add a Markets strip to Home using the attached
// image as the design reference… DIA, SPY, QQQ, IWM, BTC, ETH, and EUR/USD, in
// that order. The market strip will go directly beneath the main header."
//
// Desktop: all seven cards across the feed width, sticky under the header while
// the feed scrolls away, solid white with a subtle bottom border once stuck.
// Mobile: horizontal swipe, scrolls away with the page.
//
// Second round: no "Markets" heading or pill — just a small muted status line
// 6px above the cards; fixed card spacing (10px/12px padding, 2/6/2px between
// lines); mobile chips sized to show 2.4 at a time with a right-edge fade.
const INSTRUMENTS = [
  { label: "Dow", symbol: "DIA", ticker: "DIA" },
  { label: "S&P 500", symbol: "SPY", ticker: "SPY" },
  { label: "Nasdaq 100", symbol: "QQQ", ticker: "QQQ" },
  { label: "Small Caps", symbol: "IWM", ticker: "IWM" },
  // Client: "Remove Ether and replace it with GOLD GLD. Place the GLD chip to the
  // left of BTC." GLD is a U.S. ETF, so it follows the stock session like DIA–IWM.
  { label: "Gold", symbol: "GLD", ticker: "GLD" },
  { label: "Bitcoin", symbol: "BTC", ticker: "X:BTCUSD" },
  // Client: 'Change the label to "EURO / DOLLAR" so it fits on one line.'
  { label: "Euro / Dollar", symbol: "EUR/USD", ticker: "C:EURUSD" },
];
const TICKERS = INSTRUMENTS.map((i) => i.ticker);

// Desktop header height: the strip sticks flush beneath it.
const HEADER_OFFSET = 64;

const formatPrice = (price, ticker) => {
  if (!Number.isFinite(price)) return "–";
  const dp = priceDecimals(ticker.startsWith("X:") ? "crypto" : ticker.startsWith("C:") ? "forex" : "stock", price);
  return price.toLocaleString("en-US", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
};

// Sign from the ROUNDED value, so a -0.001% move reads 0.00%, not -0.00%.
const formatPct = (v) => {
  if (!Number.isFinite(v)) return "–";
  const r = Number(v.toFixed(2));
  return `${r > 0 ? "+" : r < 0 ? "-" : ""}${Math.abs(r).toFixed(2)}%`;
};

// Client: "From Friday 5pm to Sunday 5pm ET, append ' · Forex closed' to the
// status line… 8pm Fri - 5pm Sun: 'U.S. stocks closed · Forex closed'."
const statusLine = (session, forexOpen) => {
  if (!forexOpen && session === "closed") return "U.S. stocks closed · Forex closed";
  return `U.S. stocks · ${STOCK_SESSIONS[session]}${forexOpen ? "" : " · Forex closed"}`;
};

const MarketsStrip = ({ className = "" }) => {
  const { byTicker, live, session, forexOpen } = useMarketStrip(TICKERS);
  const ref = useRef(null);
  const rowRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  const [fade, setFade] = useState(false);

  // Right-edge fade while there is more row to swipe to; gone at the end (and
  // on the desktop grid, which never overflows).
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    const update = () =>
      setFade(row.scrollLeft + row.clientWidth < row.scrollWidth - 1);
    update();
    row.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      row.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // See body.markets-strip in index.css — lets the desktop sticky actually stick.
  useEffect(() => {
    document.body.classList.add("markets-strip");
    return () => document.body.classList.remove("markets-strip");
  }, []);

  // Bottom border only while the strip is stuck under the header (desktop).
  useEffect(() => {
    let frame = null;
    const check = () => {
      frame = null;
      const el = ref.current;
      if (!el || window.getComputedStyle(el).position !== "sticky") {
        setStuck(false);
        return;
      }
      setStuck(window.scrollY > 0 && Math.abs(el.getBoundingClientRect().top - HEADER_OFFSET) < 1);
    };
    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section
      ref={ref}
      aria-label="Markets"
      style={{ "--markets-top": `${HEADER_OFFSET}px` }}
      className={`${className} w-full bg-white md:sticky md:top-[var(--markets-top)] z-[5] md:-mx-4 md:px-4 md:w-auto pt-1 pb-2 md:pt-2 md:pb-3 border-b ${
        stuck ? "border-gray-200" : "border-transparent"
      }`}
    >
      <p className="mb-1.5 px-2 md:px-0 text-xs text-secondary leading-tight">
        {statusLine(session, forexOpen)}
      </p>

      {/* Seven across from xl (1280px) up. Below that seven cards cannot hold
          their text, so the row swipes (mobile, and tablets/small laptops).
          contain:inline-size stops the row's full scroll width from counting as
          the layout's minimum width, which otherwise widened the whole main
          column past the viewport.

          Mobile, per client: chips calc((100vw - 32px) / 2.4) wide, 8px gap,
          16px before the first card and 16px after the last — the trailing
          spacer (8px + the 8px gap), because Safari drops a scroll container's
          end padding — and a 32px fade at the right edge until the row is
          scrolled to the end. */}
      <div className="relative -mx-2 md:mx-0">
        <div
          ref={rowRef}
          data-no-pull-refresh
          className="[contain:inline-size] flex gap-2 overflow-x-auto no-scrollbar pl-4 md:pl-0 xl:grid xl:grid-cols-7 xl:overflow-visible"
        >
          {INSTRUMENTS.map((inst) => {
            const { price, changePct } = resolveQuote(byTicker[inst.ticker], live[inst.ticker], session);
            return (
              <Link
                key={inst.ticker}
                to={`/chartSearch/${inst.ticker}`}
                onClick={() => localStorage.setItem("ticker", inst.ticker)}
                className="shrink-0 w-[calc((100vw-32px)/2.4)] md:w-[136px] xl:w-auto min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2.5 hover:shadow-sm transition-shadow"
              >
                {/* Content is top-aligned and every line is single (truncate), so
                    a long label can never push one chip's rows out of line. */}
                <p className="text-[11px] font-semibold uppercase tracking-wide text-secondary leading-tight truncate">
                  {inst.label}
                </p>
                <p className="mt-0.5 text-[15px] font-bold text-primaryColor leading-tight">
                  {inst.symbol}
                </p>
                <p className="mt-1.5 text-lg font-bold text-bodyInk leading-tight tabular-nums whitespace-nowrap">
                  {formatPrice(price, inst.ticker)}
                </p>
                <p className="mt-0.5 text-sm font-semibold leading-tight tabular-nums whitespace-nowrap">
                  <span style={{ color: dayChangeColor(Number.isFinite(changePct) ? Number(changePct.toFixed(2)) : changePct) }}>{formatPct(changePct)}</span>
                </p>
              </Link>
            );
          })}
          <span aria-hidden="true" className="shrink-0 w-2 xl:hidden" />
        </div>
        {fade && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent"
          />
        )}
      </div>
    </section>
  );
};

export default MarketsStrip;
